export enum RegistrationProcessStatus {
  Error = -1,
  Unknown = 0,
  Success = 100,
}
export interface AuthInput {
  readonly identifier: string;
  readonly password: string;
}

export interface AuthToken {
  readonly token: string;
  readonly refreshToken: string;
}

export interface CompanyDomain {
  readonly uid: string;
  readonly name: string;
  readonly sysName: string;
  readonly description: string;
  readonly rowVersion: string;
}

export interface CompanyProfile {
  readonly companyLicense: number;
  readonly logoBase64: string;
  readonly companyDomain: CompanyDomain;
}

// Mirrors the backend AuthUserProfileDTO returned by GET /api/Auth/Profile.
export interface User {
  readonly uid: string;
  readonly userId: string;
  readonly userName: string;
  readonly firstName: string;
  readonly lastName: string;
  readonly middleName: string;
  readonly phoneNumber: string;
  readonly email: string;
  readonly companyPhoneNumber: string;
  readonly groupUid: string;
  readonly companyProfile: CompanyProfile;
  // Returned by the profile DTO but previously undeclared (used by the MyProfile card):
  readonly aboutMe?: string;
  readonly dateOfBirth?: string;
  readonly emailConfirmed?: boolean;
  readonly phoneNumberConfirmed?: boolean;
  readonly companyPhoneNumberConfirmed?: boolean;
  readonly rowVersion?: string;
}

export interface UserInput {
  readonly firstName?: string;
  readonly lastName?: string;
  readonly middleName?: string;
  readonly phoneNumber?: string;
  readonly email?: string;
  readonly companyPhoneNumber?: string;
  readonly aboutMe?: string;
}

export interface RefreshToken {
  readonly token: string;
  readonly refreshToken: string;
}

export interface RefreshTokenInput {
  readonly token: string;
  readonly refreshToken: string;
}

export interface ResetPasswordCodeInput {
  readonly email: string;
}

export interface RegisterUserInput {
  readonly identifier: string;
  readonly email: string;
  readonly password: string;
  readonly company: string;
  readonly termsAndConditions: boolean;
}

export interface RegisterCompanyUserInput {
  readonly companyUid: string;
  readonly usersInfos: {
    readonly firstName: string;
    readonly lastName: string;
    readonly email?: string; // optional — see C4 (login is by username, not email)
    readonly dateOfBirth: string;
    readonly roleUid: string;
    readonly departmentUid: string;
  }[];
}

export interface RegisterCompanyUserFileInput {
  readonly companyUid: string;
  readonly file: File;
}

export interface RegisterCompanyUsersPreview {
  readonly companyUid?: string;
  readonly companyName?: string;
  readonly usersInfos: {
    readonly firstName: string;
    readonly lastName: string;
    readonly email?: string;
    readonly dateOfBirth: string;
    readonly roleUid: string;
    readonly roleName: string;
    readonly departmentUid: string;
    readonly departmentName: string;
    readonly assetUid?: string;
    readonly assetName?: string;
  }[];
}

export interface RegisterCompanyUser {
  readonly status: RegistrationProcessStatus;
  readonly textLog: string[];
}

export interface ResetPasswordInput {
  readonly userUid?: string;
  readonly code?: string;
  readonly oldPassword?: string;
  readonly newPassword?: string;
}

export interface RegisterFileInput {
  readonly file: File;
  readonly companyUid?: string;
}

export interface RegisterFilePreviewInput {
  readonly file: File;
  readonly companyUid?: string;
}

// Employee

export interface RegisterCompanyEmployee {
  readonly status: RegistrationProcessStatus;
  readonly textLog: string[];
}

export interface RegisterCompanyEmployeeInput {
  readonly companyUid?: string;
  readonly employeesInfos: {
    readonly firstName: string;
    readonly lastName: string;
    readonly email?: string; // optional — see C4
    readonly dateOfBirth: string;
    readonly roleUid: string;
    readonly departmentUid: string;
    // Optional profile fields — non-blocking onboarding, enriched later (see C5)
    readonly gender?: string;
    readonly testLanguageUid?: string;
    readonly countryUid?: string;
    readonly externalRefId?: string;
  }[];
}

export interface RegisterCompanyEmployeesPreview {
  readonly companyUid?: string;
  readonly companyName?: string;
  readonly employeesInfos: {
    readonly firstName: string;
    readonly lastName: string;
    readonly email?: string;
    readonly dateOfBirth: string;
    readonly roleUid: string;
    readonly roleName: string;
    readonly departmentUid: string;
    readonly departmentName: string;
    readonly assetUid?: string;
    readonly assetName?: string;
    // Optional profile fields returned by the preview endpoint (see C5)
    readonly gender?: string;
    readonly testLanguageUid?: string;
    readonly testLanguageName?: string;
    readonly countryUid?: string;
    readonly countryName?: string;
    readonly externalRefId?: string;
  }[];
}

export interface LockUserInput {
  readonly userUid: string;
  readonly lockoutEnd?: string;
}

export interface UnlockUserInput {
  readonly userUid: string;
}
